import { z } from 'zod';

const kebabSegment = '[a-z0-9]+(?:-[a-z0-9]+)*';
const kebabPattern = new RegExp(`^${kebabSegment}$`);
const namespacedIdPattern = new RegExp(`^${kebabSegment}(?:\\.${kebabSegment}){2}$`);
const screenIdPattern = new RegExp(`^${kebabSegment}(?:/${kebabSegment})*$`);

const prohibitedIdentifierSegments = new Set([
  'mobile',
  'web',
  'ios',
  'android',
  'atlas',
  'alcohol',
  'tobacco',
  'drink',
  'drinks',
  'breathalyzer',
  'urine',
  'opioid',
  'opioids',
  'nicotine',
  'cannabis',
  'cocaine',
  'stimulant',
  'stimulants',
]);

function conditionAgnostic<T extends z.ZodType<string>>(schema: T): T {
  return schema.superRefine((value, context) => {
    if (typeof value !== 'string') return;
    const segments = value.split(/[./-]/);
    const prohibited = segments.find((segment) => prohibitedIdentifierSegments.has(segment));
    if (prohibited) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Identifier contains prohibited platform or condition segment: ${prohibited}`,
      });
    }
  });
}

export const screenIdSchema = conditionAgnostic(
  z.string().min(1).max(160).regex(screenIdPattern, 'Expected a kebab-case screen path'),
).brand<'ScreenId'>();

export const actionIdInputSchema = conditionAgnostic(
  z
    .string()
    .min(1)
    .max(160)
    .regex(namespacedIdPattern, 'Expected <domain>.<object>.<verb> in kebab-case')
    .superRefine((value, context) => {
      const object = value.split('.')[1];
      if (object && /-\d+$/.test(object)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Action object must be semantic and must not end in a positional ordinal',
        });
      }
    }),
);

export const actionIdSchema = actionIdInputSchema.brand<'ActionId'>();

export const outcomeIdSchema = conditionAgnostic(
  z
    .string()
    .min(1)
    .max(160)
    .regex(namespacedIdPattern, 'Expected <domain>.<object>.<result> in kebab-case'),
).brand<'OutcomeId'>();

export const propertyKeySchema = z
  .string()
  .min(1)
  .max(80)
  .regex(/^[a-z][a-z0-9]*(?:_[a-z0-9]+)*$/, 'Expected lowercase snake_case')
  .brand<'ProductPropertyKey'>();

function prefixedKey(prefix: 'flag' | 'experiment' | 'query') {
  return conditionAgnostic(
    z
      .string()
      .min(prefix.length + 2)
      .max(160)
      .regex(new RegExp(`^${prefix}-${kebabSegment}$`), `Expected ${prefix}-<kebab-case>`),
  );
}

export const flagKeySchema = prefixedKey('flag').brand<'FeatureFlagKey'>();
export const experimentKeySchema = prefixedKey('experiment').brand<'ExperimentKey'>();
export const queryDefinitionIdSchema = prefixedKey('query').brand<'QueryDefinitionId'>();
export const variantKeySchema = conditionAgnostic(
  z.string().min(1).max(80).regex(kebabPattern, 'Expected kebab-case variant key'),
).brand<'VariantKey'>();

export type ScreenId = z.infer<typeof screenIdSchema>;
export type ActionId = z.infer<typeof actionIdSchema>;
export type OutcomeId = z.infer<typeof outcomeIdSchema>;
export type ProductPropertyKey = z.infer<typeof propertyKeySchema>;
export type FeatureFlagKey = z.infer<typeof flagKeySchema>;
export type ExperimentKey = z.infer<typeof experimentKeySchema>;
export type QueryDefinitionId = z.infer<typeof queryDefinitionIdSchema>;
export type VariantKey = z.infer<typeof variantKeySchema>;
